import { Alert, Button, Card, Form, Input, Modal, Select, Space, Tag, Typography } from "antd";
import type { Customer, Page, Ship } from "@zhihaoscm/entity";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Pagination as ZhPagination, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { customerService } from "@/api/services/customerService";
import { DEFAULT_VALUE } from "@/config";
import { YES_OR_NO } from "@/meta/common";
import { CANOPY_STATE, RECOGNIZE_RESULT, SHIP_INFO_STATE } from "@/meta/logistics/shipInfo";
import { RELATED_DEVICE } from "@/meta/logistics/ship";
import { ONLINE } from "@/meta/logistics/device";
import { useUserStore } from "@/stores/react/authStore";

export interface ShipInfoQuery {
    page: number;
    size: number;
    param?: string;
    relatedDevice?: number;
    existMobile?: number;
    contactMaintainer?: string;
    existWaterGauge?: number;
    warehouseState?: number;
    canopyState?: number;
    state?: number;
    carrier?: number;
    sortKey?: string;
    sortOrder?: string;
}

export interface ShipInfoRow {
    ship: Partial<Ship> & { id?: string | number };
    devices?: Array<{ id?: number | string; name?: string; online?: number }>;
    customer?: Partial<Customer>;
    existDevice?: number;
    existMobile?: number;
    existWaterGauge?: number;
}

export interface ShipInfoPageProps {
    loadShips?: (query: ShipInfoQuery) => Promise<Page<ShipInfoRow>>;
    loadShipPreview?: (mmsi: string) => Promise<{ ship?: Partial<Ship>; exist: boolean }>;
    createShip?: (mmsi: string) => Promise<unknown>;
    unbindShip?: (id: string | number) => Promise<void>;
    permissions?: string[];
    banButton?: boolean;
}

/** 复用 Vue 船舶信息列表的物流域分页接口。 */
export async function loadShipInfo(query: ShipInfoQuery): Promise<Page<ShipInfoRow>> {
    const response = await shippingRequestHttp.service.get<Page<ShipInfoRow>>("/ship/paging", { params: query });
    return response.data;
}

/** 复用 Vue 新增弹窗的 AIS 预览和重复船舶校验接口。 */
export async function loadShipPreview(mmsi: string) {
    const response = await shippingRequestHttp.service.get<{ ship?: Partial<Ship>; exist: boolean }>(
        `/ship/find-validate/${encodeURIComponent(mmsi)}`
    );
    return response.data;
}

/** 复用 Vue 新增船舶接口，仅提交 MMSI 主键。 */
export async function createShip(mmsi: string) {
    const response = await shippingRequestHttp.service.post("/ship", { id: mmsi });
    return response.data;
}

/** 复用旧 Vue 船舶列表的解绑接口，保持列表操作与详情操作一致。 */
export async function unbindShipInfo(id: string | number): Promise<void> {
    await shippingRequestHttp.service.put(`/ship/unbind/${encodeURIComponent(id)}`);
}

/** 复用旧 Vue 承运商远程选择器，保留 personalAuth=1 的筛选契约。 */
export async function loadShipCarrierOptions(keyword: string): Promise<Array<{ value: number; label: string }>> {
    const customers = await customerService.customerSelector({ param: keyword, personalAuth: YES_OR_NO.YES.code });
    return customers.slice(0, 50).map((customer: Customer) => ({
        value: Number(customer.id),
        label: customer.institutionName || customer.realName || String(customer.id)
    }));
}

// 旧 Vue getTableList 在未选择摄像头条件时补充“全部”关系值。
const defaultQuery: ShipInfoQuery = { page: 1, size: 20, relatedDevice: RELATED_DEVICE.ALL.code };

function text(value: unknown): string {
    return value == null || value === "" ? DEFAULT_VALUE : String(value);
}

/** 旧 Vue DATETIME_PICKER_FORMAT 只展示到分钟，避免列表暴露秒级差异。 */
function dateText(value: unknown): string {
    return typeof value === "string" && value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

function enumText(meta: { getIntroByCode: (code?: number) => string }, value?: number): string {
    return meta.getIntroByCode(value) || DEFAULT_VALUE;
}

function shipName(row: ShipInfoRow): string {
    return text(row.ship.cnname || row.ship.name);
}

function carrierName(row: ShipInfoRow): string {
    return text(row.customer?.institutionName || row.customer?.realName);
}

function mobileText(value?: string): string {
    if (!value) return DEFAULT_VALUE;
    return value.replace(/(\d{3})\d{4}(\d{4})/, "$1****$2");
}

function deviceText(row: ShipInfoRow): string {
    if (!row.devices?.length) return DEFAULT_VALUE;
    return row.devices.map(device => device.name || DEFAULT_VALUE).join("、");
}

function deviceOnlineState(device: { online?: number }): string {
    return device.online === ONLINE.ONLINE.code ? "在线" : "离线";
}

/** 物流管理-船舶信息列表，覆盖 Vue 首屏查询、分页和核心字段。 */
export function ShipInfoPage({
    loadShips = loadShipInfo,
    loadShipPreview: loadPreview = loadShipPreview,
    createShip: create = createShip,
    unbindShip: unbind = unbindShipInfo,
    permissions,
    banButton
}: ShipInfoPageProps) {
    const navigate = useNavigate();
    const storeBanButton = useUserStore(state => state.banButton);
    const isButtonBanned = banButton ?? storeBanButton;
    // 未传入权限时兼容旧测试/嵌入入口；路由入口由页面权限配置负责限制访问。
    const can = (permission: string) => permissions === undefined || permissions.includes(permission);
    const [rows, setRows] = useState<ShipInfoRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<ShipInfoQuery>(defaultQuery);
    const [reloadVersion, setReloadVersion] = useState(0);
    const [filters, setFilters] = useState<ShipInfoQuery>(defaultQuery);
    const [errorMessage, setErrorMessage] = useState("");
    const [addOpen, setAddOpen] = useState(false);
    const [mmsi, setMmsi] = useState("");
    const [preview, setPreview] = useState<{ ship?: Partial<Ship>; exist: boolean } | null>(null);
    const [addError, setAddError] = useState("");
    const [carrierOptions, setCarrierOptions] = useState<Array<{ value: number; label: string }>>([]);

    useEffect(() => {
        let disposed = false;
        void loadShips(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("船舶信息加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadShips, query, reloadVersion]);

    const setFilter = <K extends keyof ShipInfoQuery>(key: K, value: ShipInfoQuery[K]) => {
        setFilters(current => ({ ...current, [key]: value }));
    };

    /** 按旧 Vue 列表的字段命名提交筛选，并回到第一页。 */
    const submitFilters = () => {
        const nextQuery: Record<string, unknown> = {
            page: 1,
            size: 20,
            relatedDevice: filters.relatedDevice ?? RELATED_DEVICE.ALL.code
        };
        Object.entries(filters).forEach(([key, value]) => {
            if (key === "page" || key === "size" || key === "relatedDevice" || value === "" || value == null) return;
            nextQuery[key] = value;
        });
        setQuery(nextQuery as unknown as ShipInfoQuery);
    };

    const resetFilters = () => {
        setFilters(defaultQuery);
        setQuery(defaultQuery);
    };

    /** 按旧 Vue ProTable 的 createdTime 排序参数重新查询第一页。 */
    const handleSortChange = ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        if (prop !== "ship.createdTime") return;
        setQuery(current => ({
            ...current,
            page: 1,
            sortKey: order ? "created_time" : undefined,
            sortOrder: order === "ascending" ? "asc" : order === "descending" ? "desc" : undefined
        }));
    };

    /** 仅在输入承运商关键词后远程查询旧 Vue 客户选择器。 */
    const handleCarrierSearch = async (keyword: string) => {
        if (!keyword.trim()) {
            setCarrierOptions([]);
            return;
        }
        try {
            setCarrierOptions(await loadShipCarrierOptions(keyword.trim()));
        } catch {
            setCarrierOptions([]);
        }
    };

    /** 解绑列表中的认证船舶，并沿用旧列表的刷新行为。 */
    const handleUnbind = async (shipId: string | number) => {
        try {
            await unbind(shipId);
            setReloadVersion(current => current + 1);
        } catch {
            setErrorMessage("船舶解绑失败");
        }
    };

    /** 输入完整 9 位 MMSI 后沿用 Vue 接口展示 AIS 预览。 */
    const handleMmsiChange = (value: string) => {
        setMmsi(value.slice(0, 9));
        setPreview(null);
        setAddError("");
        if (/^\d{9}$/.test(value)) {
            void loadPreview(value)
                .then(result => setPreview(result))
                .catch(() => setAddError("AIS信息查询失败"));
        }
    };

    /** 按 Vue 表单规则校验 MMSI，避免非法或重复船舶创建。 */
    const handleCreate = async () => {
        if (!mmsi) {
            setAddError("请输入MMSI");
            return;
        }
        if (!/^\d{9}$/.test(mmsi)) {
            setAddError("请输入正确的MMSI");
            return;
        }
        if (preview?.exist) return;
        try {
            await create(mmsi);
            setAddOpen(false);
            setMmsi("");
            setPreview(null);
            setAddError("");
            setReloadVersion(current => current + 1);
        } catch {
            setAddError("船舶创建失败");
        }
    };

    /** 关闭新增弹窗并清理上一次 MMSI 预览。 */
    const closeAdd = () => {
        setAddOpen(false);
        setMmsi("");
        setPreview(null);
        setAddError("");
    };

    // 用 zhihao-ui-react 的列契约承接旧 Vue 船舶信息 ZhTable 的字段与操作插槽。
    const shipColumns = useMemo<TableColumnProps<ShipInfoRow>[]>(
        () => [
            {
                key: "id",
                label: "MMSI",
                prop: "ship.id",
                render: ({ row }) => (
                    <Link to={`/logistics/shipManage/shipInfo/shipInfoDetail/${row.ship.id}`}>{text(row.ship.id)}</Link>
                )
            },
            { key: "name", label: "AIS船名", prop: "ship.name", render: ({ row }) => shipName(row) },
            { key: "carrier", label: "承运商", prop: "customer", render: ({ row }) => carrierName(row) },
            {
                key: "devices",
                label: "摄像头",
                prop: "devices",
                render: ({ row }) => (
                    <Space>
                        <span>{deviceText(row)}</span>
                        {row.devices?.map(device => (
                            <span
                                key={String(device.id)}
                                aria-label={`设备在线状态-${device.id}`}
                                title={deviceOnlineState(device)}
                            >
                                ●
                            </span>
                        ))}
                    </Space>
                )
            },
            { key: "mobile", label: "联系方式", prop: "ship.mobile", render: ({ row }) => mobileText(row.ship.mobile) },
            {
                key: "contactMaintainer",
                label: "联系方式维护人",
                prop: "ship.contactMaintainer",
                render: ({ row }) => text(row.ship.contactMaintainer)
            },
            {
                key: "existWaterGauge",
                label: "是否维护水尺",
                prop: "existWaterGauge",
                render: ({ row }) => enumText(YES_OR_NO, row.existWaterGauge)
            },
            {
                key: "dataProgress",
                label: "完善进度",
                prop: "ship.dataProgress",
                render: ({ row }) => `${text(row.ship.dataProgress)}%`
            },
            {
                key: "warehouseState",
                label: "货仓状态",
                prop: "ship.warehouseState",
                render: ({ row }) => (
                    <Space>
                        <span>{enumText(RECOGNIZE_RESULT, row.ship.warehouseState)}</span>
                        {(Number(row.ship.warehouseState) === RECOGNIZE_RESULT.LOADING.code ||
                            Number(row.ship.warehouseState) === RECOGNIZE_RESULT.UNLOADING.code) &&
                        row.ship.remainingLayTime != null ? (
                            <span>预计剩余{row.ship.remainingLayTime}h</span>
                        ) : null}
                    </Space>
                )
            },
            {
                key: "canopyState",
                label: "棚架状态",
                prop: "ship.canopyState",
                render: ({ row }) => enumText(CANOPY_STATE, row.ship.canopyState)
            },
            {
                key: "state",
                label: "认证状态",
                prop: "ship.state",
                render: ({ row }) => <Tag>{enumText(SHIP_INFO_STATE, row.ship.state)}</Tag>
            },
            {
                key: "createdTime",
                label: "创建时间",
                prop: "ship.createdTime",
                sortable: true,
                render: ({ row }) => dateText(row.ship.createdTime)
            },
            {
                key: "operation",
                label: "操作",
                prop: "operation",
                fixed: "right",
                render: ({ row }) => (
                    <Space>
                        {can("ROLE_SHIP_W") ? (
                            <Link to={`/logistics/shipManage/shipInfo/shipInfoEdit/${row.ship.id}`}>
                                <Button disabled={isButtonBanned} type="link">
                                    修改
                                </Button>
                            </Link>
                        ) : null}
                        {can("ROLE_SHIP_W") ? (
                            Number(row.ship.state) === SHIP_INFO_STATE.CERTIFIED.code ? (
                                <Button
                                    aria-label={`解绑-${row.ship.id}`}
                                    disabled={isButtonBanned}
                                    type="link"
                                    onClick={() => void handleUnbind(String(row.ship.id))}
                                >
                                    解绑
                                </Button>
                            ) : (
                                <Button
                                    aria-label={`绑定-${row.ship.id}`}
                                    disabled={isButtonBanned}
                                    type="link"
                                    onClick={() =>
                                        navigate(`/logistics/shipManage/shipInfo/shipInfoBind/${row.ship.id}/1`)
                                    }
                                >
                                    绑定
                                </Button>
                            )
                        ) : null}
                        {row.devices?.length && can("ROLE_SHIP_R") ? (
                            <Button
                                aria-label={`监控-${row.ship.id}`}
                                disabled={isButtonBanned}
                                type="link"
                                onClick={() => navigate(`/logistics/shipManage/shipInfo/shipInfoDetail/${row.ship.id}`)}
                            >
                                监控
                            </Button>
                        ) : null}
                    </Space>
                )
            }
        ],
        [can, handleUnbind, isButtonBanned, navigate]
    );

    return (
        <section aria-label="ship-info-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Form layout="inline" onFinish={submitFilters}>
                    <Form.Item label="MMSI">
                        <Input
                            aria-label="MMSI"
                            placeholder="请输入船舶名关键词或MMSI搜索"
                            value={filters.param || ""}
                            onChange={event => setFilter("param", event.target.value)}
                        />
                    </Form.Item>
                    <Form.Item label="承运商">
                        <Select<number>
                            aria-label="承运商"
                            allowClear
                            showSearch
                            filterOption={false}
                            placeholder="请选择承运商"
                            value={filters.carrier}
                            options={carrierOptions}
                            onSearch={value => void handleCarrierSearch(value)}
                            onChange={value => setFilter("carrier", value)}
                        />
                    </Form.Item>
                    <Form.Item label="摄像头">
                        <Select
                            aria-label="摄像头"
                            allowClear
                            placeholder="请选择是否绑定摄像头"
                            value={filters.relatedDevice}
                            options={[
                                { value: RELATED_DEVICE.YES.code, label: "已绑定" },
                                { value: RELATED_DEVICE.NO.code, label: "未绑定" }
                            ]}
                            onChange={value => setFilter("relatedDevice", value)}
                        />
                    </Form.Item>
                    <Form.Item label="联系方式维护人">
                        <Input
                            aria-label="联系方式维护人"
                            placeholder="请输入联系方式维护人"
                            value={filters.contactMaintainer || ""}
                            onChange={event => setFilter("contactMaintainer", event.target.value)}
                        />
                    </Form.Item>
                    <Form.Item label="联系方式">
                        <Select
                            aria-label="联系方式"
                            allowClear
                            placeholder="是否有联系方式"
                            value={filters.existMobile}
                            options={YES_OR_NO.options.map(item => ({ value: item.value, label: item.label }))}
                            onChange={value => setFilter("existMobile", value)}
                        />
                    </Form.Item>
                    <Form.Item label="是否维护水尺">
                        <Select
                            aria-label="是否维护水尺"
                            allowClear
                            placeholder="请选择是否维护水尺"
                            value={filters.existWaterGauge}
                            options={YES_OR_NO.options.map(item => ({ value: item.value, label: item.label }))}
                            onChange={value => setFilter("existWaterGauge", value)}
                        />
                    </Form.Item>
                    <Form.Item label="货仓状态">
                        <Select
                            aria-label="货仓状态"
                            allowClear
                            placeholder="请选择货仓状态"
                            value={filters.warehouseState}
                            options={RECOGNIZE_RESULT.options.map(item => ({ value: item.value, label: item.label }))}
                            onChange={value => setFilter("warehouseState", value)}
                        />
                    </Form.Item>
                    <Form.Item label="棚架状态">
                        <Select
                            aria-label="棚架状态"
                            allowClear
                            placeholder="请选择棚架状态"
                            value={filters.canopyState}
                            options={CANOPY_STATE.options.map(item => ({ value: item.value, label: item.label }))}
                            onChange={value => setFilter("canopyState", value)}
                        />
                    </Form.Item>
                    <Form.Item label="认证状态">
                        <Select
                            aria-label="认证状态"
                            allowClear
                            placeholder="请选择认证状态"
                            value={filters.state}
                            options={SHIP_INFO_STATE.options.map(item => ({ value: item.value, label: item.label }))}
                            onChange={value => setFilter("state", value)}
                        />
                    </Form.Item>
                    <Space>
                        <Button aria-label="查询" htmlType="submit" type="primary">
                            查询
                        </Button>
                        <Button aria-label="重置" onClick={resetFilters}>
                            重置
                        </Button>
                    </Space>
                </Form>
            </Card>
            <Card>
                <ZhTable<ShipInfoRow>
                    rowKey={row => String(row.ship.id)}
                    data={rows}
                    columns={shipColumns}
                    emptyText="暂无船舶信息"
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Space className="mb-4 w-full" align="center">
                                <Typography.Title className="mb-0" level={4}>
                                    船舶信息
                                </Typography.Title>
                                {can("ROLE_SHIP_W") ? (
                                    <Button
                                        aria-label="新增"
                                        disabled={isButtonBanned}
                                        type="primary"
                                        onClick={() => setAddOpen(true)}
                                    >
                                        新增
                                    </Button>
                                ) : null}
                                {can("ROLE_SHIP_I") ? (
                                    <Button
                                        aria-label="导入联系方式"
                                        disabled={isButtonBanned}
                                        type="primary"
                                        onClick={() =>
                                            navigate("/logistics/shipManage/shipInfo/contactInformationUpload")
                                        }
                                    >
                                        导入联系方式
                                    </Button>
                                ) : null}
                            </Space>

                            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                total={total}
                                showSizeChanger
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        // 复用 Vue ProTable 的分页契约：切换条数后从第 1 页重新查询。
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <Modal
                open={addOpen}
                title="新增船舶"
                onCancel={closeAdd}
                onOk={() => void handleCreate()}
                okText="提交"
                cancelText="取消"
                okButtonProps={{ autoInsertSpace: false, style: preview?.exist ? { display: "none" } : undefined }}
            >
                <Form layout="vertical">
                    <Form.Item label="MMSI" required>
                        <Input
                            aria-label="新增船舶MMSI"
                            value={mmsi}
                            maxLength={9}
                            placeholder="请输入MMSI"
                            onChange={event => handleMmsiChange(event.target.value)}
                        />
                    </Form.Item>
                </Form>
                {addError ? <Alert title={addError} role="alert" type="error" /> : null}
                {preview ? (
                    <div aria-label="ship-preview" className="rounded border p-4">
                        <Space orientation="vertical">
                            <Typography.Text strong>AIS信息</Typography.Text>
                            {preview.exist ? <Typography.Text type="danger">船舶已存在</Typography.Text> : null}
                            <span>船名：{preview.ship?.name || "-"}</span>
                            <span>MMSI：{preview.ship?.id || mmsi}</span>
                        </Space>
                    </div>
                ) : null}
            </Modal>
        </section>
    );
}

export default ShipInfoPage;
